import { useMemo, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Icon } from "@iconify/react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import MuiConfirmationDialog from "../../@material-extend/MuiConfirmationDialog";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import sanitizeValues from "src/utils/sanitizeValues";
import {
  getTeamUrl,
  fleetDriversUrl,
  fleetDriverIdUrl,
  // fleetRfidMappingsUrl,
  // fleetRfidMappingIdUrl,
} from "src/react-query/endPoints";
import {
  FLEET_ENTITY_STATUS_OPTIONS,
  fleetDriverValidationSchema,
  getDriverDefaultValues,
  toList,
  fullName,
  statusChipColor,
  emptyToNull,
} from "../fleet.constants";

const getDriverId = (row) => row?.driver_id ?? row?.driverId ?? row?.id;
const getMappingId = (row) => row?.mapping_id ?? row?.mappingId ?? row?.id;

const DriversTab = ({ operatorId }) => {
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const hasDeleteAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "D");

  const {
    data: driversData,
    isFetching,
  } = useList("fleetDrivers", undefined, operatorId);
  const {
    data: vehiclesData,
  } = useList("fleetVehicles", undefined, operatorId);
  /* RFID Mappings — commented out
  const {
    data: mappingsData,
    isFetching: isMappingsFetching,
    refetch: refetchMappings,
  } = useList("fleetRfidMappings", undefined, operatorId);
  */

  const drivers = useMemo(() => toList(driversData), [driversData]);
  const vehicles = useMemo(() => toList(vehiclesData), [vehiclesData]);
  // const mappings = useMemo(() => toList(mappingsData), [mappingsData]); // RFID Mappings — commented out

  const { mutate: mutateDriver, isLoading: isDriverMutating } = useMutate(
    "fleetDrivers",
    { id: operatorId }
  );
  /* RFID Mappings — commented out
  const { mutate: mutateMapping, isLoading: isMappingMutating } = useMutate(
    "fleetRfidMappings",
    { id: operatorId }
  );
  */

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  /* RFID Mappings — commented out
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingForm, setMappingForm] = useState({
    id_tag: "",
    driver_id: "",
    vehicle_id: "",
  });
  const [revokingMapping, setRevokingMapping] = useState(null);
  */

  const openCreate = () => {
    setEditingDriver(null);
    formik.resetForm({ values: getDriverDefaultValues() });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingDriver(row);
    const defaults = getDriverDefaultValues();
    formik.resetForm({
      values: {
        ...defaults,
        ...Object.keys(defaults).reduce((acc, key) => {
          acc[key] = row?.[key] ?? defaults[key] ?? "";
          return acc;
        }, {}),
        license_expiry_date: row?.license_expiry_date
          ? String(row.license_expiry_date).slice(0, 10)
          : "",
      },
    });
    setDialogOpen(true);
  };

  const onSubmit = (values, { setSubmitting }) => {
    const body = emptyToNull(sanitizeValues(values));
    const isEdit = Boolean(editingDriver);
    mutateDriver(
      {
        url: isEdit
          ? getTeamUrl(teamId, fleetDriverIdUrl(operatorId, getDriverId(editingDriver)))
          : getTeamUrl(teamId, fleetDriversUrl(operatorId)),
        method: isEdit ? "PUT" : "POST",
        body,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getDriverDefaultValues(),
    validationSchema: fleetDriverValidationSchema,
    onSubmit,
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  const onDeleteDriver = () => {
    mutateDriver(
      {
        url: getTeamUrl(
          teamId,
          fleetDriverIdUrl(operatorId, getDriverId(deletingDriver))
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setDeletingDriver(null);
        },
      }
    );
  };

  /* RFID Mappings — commented out
  const onAddMapping = () => {
    mutateMapping(
      {
        url: getTeamUrl(teamId, fleetRfidMappingsUrl(operatorId)),
        method: "POST",
        body: emptyToNull(sanitizeValues(mappingForm)),
      },
      {
        onSuccess: () => {
          setMappingDialogOpen(false);
          setMappingForm({ id_tag: "", driver_id: "", vehicle_id: "" });
          refetchMappings();
        },
      }
    );
  };

  const onRevokeMapping = () => {
    mutateMapping(
      {
        url: getTeamUrl(
          teamId,
          fleetRfidMappingIdUrl(operatorId, getMappingId(revokingMapping))
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setRevokingMapping(null);
          refetchMappings();
        },
      }
    );
  };
  */

  /* RFID Mappings — commented out
  const driverNameById = useMemo(() => {
    return drivers.reduce((acc, driver) => {
      acc[getDriverId(driver)] = fullName(driver);
      return acc;
    }, {});
  }, [drivers]);

  const vehicleLabelById = useMemo(() => {
    return vehicles.reduce((acc, vehicle) => {
      const vid = vehicle?.vehicle_id ?? vehicle?.vehicleId ?? vehicle?.id;
      acc[vid] = vehicle?.registration_number ?? `#${vid}`;
      return acc;
    }, {});
  }, [vehicles]);
  */

  const driverColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        valueGetter: ({ row }) => fullName(row),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 0.8,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "license_number",
        headerName: "License #",
        flex: 0.8,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "license_expiry_date",
        headerName: "License Expiry",
        flex: 0.8,
        renderCell: ({ value }) =>
          value ? fDateTime(value, "dd MMM yyyy") : "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row">
            {hasUpdateAccess && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)}>
                  <Icon icon="eva:edit-outline" />
                </IconButton>
              </Tooltip>
            )}
            {hasDeleteAccess && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setDeletingDriver(row)}>
                  <Icon icon="eva:trash-2-outline" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpdateAccess, hasDeleteAccess]
  );

  /* RFID Mappings — commented out
  const mappingColumns = useMemo(
    () => [
      {
        field: "id_tag",
        headerName: "RFID Tag",
        flex: 1,
      },
      {
        field: "driver_id",
        headerName: "Driver",
        flex: 1,
        renderCell: ({ value }) =>
          value ? driverNameById[value] ?? `#${value}` : "-",
      },
      {
        field: "vehicle_id",
        headerName: "Vehicle",
        flex: 1,
        renderCell: ({ value }) =>
          value ? vehicleLabelById[value] ?? `#${value}` : "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        renderCell: ({ row }) => (
          <>
            {hasDeleteAccess && !isScopedOperator && (
              <Tooltip title="Remove Mapping">
                <IconButton size="small" onClick={() => setRevokingMapping(row)}>
                  <Icon icon="eva:trash-2-outline" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
    ],
    [driverNameById, vehicleLabelById, hasDeleteAccess, isScopedOperator]
  );
  */

  const dialogTextField = (name, label, extra = {}) => (
    <FormControl fullWidth size="small">
      <TextField
        size="small"
        label={label}
        {...getFieldProps(name)}
        error={Boolean(touched[name] && errors[name])}
        helperText={touched[name] && errors[name]}
        {...extra}
      />
    </FormControl>
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Drivers</Typography>
        {hasCreateAccess && (
          <Button
            variant="contained"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={openCreate}
          >
            Add Driver
          </Button>
        )}
      </Stack>
      <BaseTable
        rows={drivers}
        columns={driverColumns}
        loading={isFetching}
        getRowId={(row) => getDriverId(row)}
        pageSize={10}
      />
      {!isFetching && !drivers.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No drivers added yet.
        </Typography>
      )}

      {/* RFID Mappings — commented out
      <Divider sx={{ my: 3 }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">RFID Mappings</Typography>
        {hasCreateAccess && !isScopedOperator && (
          <Button
            variant="outlined"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={() => setMappingDialogOpen(true)}
          >
            Map RFID Tag
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Charging sessions started with a mapped RFID tag are attributed to this
        fleet operator (and optionally a driver/vehicle).
      </Typography>
      <BaseTable
        rows={mappings}
        columns={mappingColumns}
        loading={isMappingsFetching}
        getRowId={(row) => getMappingId(row)}
        pageSize={10}
      />
      {!isMappingsFetching && !mappings.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No RFID tags mapped yet.
        </Typography>
      )}
      */}

      {/* Add / edit driver dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingDriver ? "Edit Driver" : "Add Driver"}
        </DialogTitle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("first_name", "First Name", { required: true })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("last_name", "Last Name", { required: true })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("email", "Email", { type: "email" })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("phone", "Phone")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("license_number", "License Number")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("license_expiry_date", "License Expiry", {
                    type: "date",
                    InputLabelProps: { shrink: true },
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="fleet-driver-status">Status</InputLabel>
                    <Select
                      labelId="fleet-driver-status"
                      label="Status"
                      {...getFieldProps("status")}
                    >
                      {FLEET_ENTITY_STATUS_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isDriverMutating}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>

      {/* Map RFID tag dialog — commented out
      <Dialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Map RFID Tag</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              size="small"
              label="RFID Tag (id_tag)"
              required
              value={mappingForm.id_tag}
              onChange={(e) =>
                setMappingForm((form) => ({ ...form, id_tag: e.target.value }))
              }
              helperText="Must be an existing OCPP tag id"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="mapping-driver">Driver (optional)</InputLabel>
              <Select
                labelId="mapping-driver"
                label="Driver (optional)"
                value={mappingForm.driver_id}
                onChange={(e) =>
                  setMappingForm((form) => ({
                    ...form,
                    driver_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">None</MenuItem>
                {drivers.map((driver) => (
                  <MenuItem
                    key={getDriverId(driver)}
                    value={getDriverId(driver)}
                  >
                    {fullName(driver)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="mapping-vehicle">Vehicle (optional)</InputLabel>
              <Select
                labelId="mapping-vehicle"
                label="Vehicle (optional)"
                value={mappingForm.vehicle_id}
                onChange={(e) =>
                  setMappingForm((form) => ({
                    ...form,
                    vehicle_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">None</MenuItem>
                {vehicles.map((vehicle) => {
                  const vid =
                    vehicle?.vehicle_id ?? vehicle?.vehicleId ?? vehicle?.id;
                  return (
                    <MenuItem key={vid} value={vid}>
                      {vehicle?.registration_number ?? `#${vid}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setMappingDialogOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!mappingForm.id_tag.trim()}
            loading={isMappingMutating}
            onClick={onAddMapping}
          >
            Map Tag
          </LoadingButton>
        </DialogActions>
      </Dialog>
      */}

      {/* Delete driver confirm */}
      <MuiConfirmationDialog
        open={Boolean(deletingDriver)}
        onClose={() => setDeletingDriver(null)}
        title="Delete Driver"
        severity="error"
        contentText={`Are you sure you want to delete driver "${fullName(
          deletingDriver
        )}"?`}
        closeText="Cancel"
        okText="Delete"
        onOk={onDeleteDriver}
        isLoading={isDriverMutating}
      />

      {/* Revoke mapping confirm — commented out
      <MuiConfirmationDialog
        open={Boolean(revokingMapping)}
        onClose={() => setRevokingMapping(null)}
        title="Remove RFID Mapping"
        severity="warning"
        contentText={`Remove mapping for tag "${revokingMapping?.id_tag ?? ""}"?`}
        closeText="Cancel"
        okText="Remove"
        onOk={onRevokeMapping}
        isLoading={isMappingMutating}
      />
      */}
    </>
  );
};

export default DriversTab;
